import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "@phosphor-icons/react";
import { useLogin, useRegister, useTelegramLogin } from "./auth";
import { useTwoFAChallenge, useTwoFARecoveryRequest } from "./twoFactorApi";
import { pollTgLinkStatus, useTgLinkConsume, useTgLinkStart } from "./tgLinkApi";
import { getTg } from "./useTelegramWebApp";
import { Trans, useTranslation } from "react-i18next";
import { trackEvent, PlausibleEvent } from "./plausible";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { t, i18n } = useTranslation("auth");

  const redirectTo = (() => {
    try {
      const persisted = sessionStorage.getItem("postLoginRedirect");
      if (persisted) {
        sessionStorage.removeItem("postLoginRedirect");
        return persisted;
      }
    } catch {
      // silent: sessionStorage unavailable (private mode / quota)
    }
    return (loc.state as { from?: string } | null)?.from || "/";
  })();
  const qc = useQueryClient();
  const login = useLogin();
  const register = useRegister();
  const tgLogin = useTelegramLogin();
  const tgLinkStart = useTgLinkStart();
  const tgLinkConsume = useTgLinkConsume();

  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [healthConsent, setHealthConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoTrying, setAutoTrying] = useState(false);

  // F2 sess post-launch: 2FA challenge state. Set when /login returns
  // 401 detail.reason="2fa_required" and a tmp_token. We don't store it in
  // sessionStorage — losing it on refresh forces password re-entry, which
  // matches typical 2FA UX (and is safer than persisting).
  const [twoFATmpToken, setTwoFATmpToken] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState("");
  const twoFAChallenge = useTwoFAChallenge();
  const twoFARecovery = useTwoFARecoveryRequest();
  const [recoverySent, setRecoverySent] = useState(false);

  const [waitingForTg, setWaitingForTg] = useState(() => !!sessionStorage.getItem("tg_login_token"));
  const [waitingDeeplink, setWaitingDeeplink] = useState<string | null>(null);
  // Pre-fetched TG login token so the Telegram button can be a real <a> anchor.
  // A genuine anchor tap is never popup-blocked and opens the TG app reliably
  // on mobile — unlike window.open() called after an await (which mobile Safari
  // silently drops because the user-gesture context is already gone).
  const [tgToken, setTgToken] = useState<string | null>(null);
  const [tgDeeplink, setTgDeeplink] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);

  // W4.6 — TG WebApp consent modal state. The auto-login below fires with
  // consent_accepted=false on first open. If the backend responds 400
  // "consent_required" → it's a brand-new TG user; we show the modal,
  // wait for the checkbox, and retry with consent_accepted=true.
  // Returning users (already in DB) bypass the gate entirely.
  const [tgPendingInitData, setTgPendingInitData] = useState<string | null>(null);

  useEffect(() => {
    const tg = getTg();
    if (tg?.initData && tg.initData.length > 0) {
      setAutoTrying(true);
      tg.ready();
      tgLogin.mutate(
        { initData: tg.initData, consent_accepted: false },
        {
          onSuccess: () => nav(redirectTo, { replace: true }),
          onError: (e: any) => {
            const detail = e?.response?.data?.detail;
            // W4.6 — first-time TG user → ask for consent, retry on accept
            if (
              e?.response?.status === 400 &&
              typeof detail === "string" &&
              detail.startsWith("consent_required")
            ) {
              setTgPendingInitData(tg.initData);
              setAutoTrying(false);
              return;
            }
            setError(detail || t("err_tg_auth"));
            setAutoTrying(false);
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptTgConsent = () => {
    if (!tgPendingInitData) return;
    setAutoTrying(true);
    tgLogin.mutate(
      { initData: tgPendingInitData, consent_accepted: true },
      {
        onSuccess: () => {
          setTgPendingInitData(null);
          nav(redirectTo, { replace: true });
        },
        onError: (e: any) => {
          setError(e?.response?.data?.detail || t("err_tg_auth"));
          setAutoTrying(false);
          setTgPendingInitData(null);
        },
      },
    );
  };

  // Pre-create a TG login token on mount so the button is a real anchor.
  useEffect(() => {
    const tg = getTg();
    if (tg?.initData && tg.initData.length > 0) return; // inside TG webapp
    if (sessionStorage.getItem("tg_login_token")) return; // already waiting
    let cancelled = false;
    (async () => {
      try {
        const { token, deeplink } = await tgLinkStart.mutateAsync();
        if (!cancelled) {
          setTgToken(token);
          setTgDeeplink(deeplink);
        }
      } catch {
        // silent: falls back to on-demand fetch
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const savedToken = sessionStorage.getItem("tg_login_token");
    if (savedToken) {
      startedAtRef.current = parseInt(sessionStorage.getItem("tg_login_started") || "0", 10) || Date.now();
      pollRef.current = window.setInterval(async () => {
        if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
          sessionStorage.removeItem("tg_login_token");
          sessionStorage.removeItem("tg_login_started");
          stopWaiting(t("err_timeout"));
          return;
        }
        try {
          const st = await pollTgLinkStatus(savedToken);
          if (st.confirmed) {
            if (pollRef.current) window.clearInterval(pollRef.current);
            pollRef.current = null;
            sessionStorage.removeItem("tg_login_token");
            sessionStorage.removeItem("tg_login_started");
            const athlete = await tgLinkConsume.mutateAsync(savedToken);
            qc.setQueryData(["me"], athlete);
            nav(redirectTo, { replace: true });
          }
        } catch (e: any) {
          if (e?.response?.status === 410) {
            sessionStorage.removeItem("tg_login_token");
            sessionStorage.removeItem("tg_login_started");
            stopWaiting(t("err_expired"));
          }
        }
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPendingReferral = async () => {
    try {
      const code = sessionStorage.getItem("pending_referral");
      if (!code) return;
      const { api } = await import("./api");
      try { await api.post(`/athlete/referral/redeem/${code}`); } catch {
        // silent: referral redeem is fire-and-forget; failure is non-blocking for login flow
      }
      try { sessionStorage.removeItem("pending_referral"); } catch {
        // silent: sessionStorage unavailable (private mode / quota)
      }
    } catch {
      // silent: sessionStorage unavailable (private mode / quota)
    }
  };

  const navAfterAuth = () => {
    let target = redirectTo;
    try {
      const ev = sessionStorage.getItem("pending_event");
      if (ev) {
        target = `/events/${ev}`;
        sessionStorage.removeItem("pending_event");
      }
    } catch {
      // silent: sessionStorage unavailable (private mode / quota)
    }
    nav(target, { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === "register" && !healthConsent) {
      setError(t("err_consent_required"));
      return;
    }
    const handlers = {
      onSuccess: async () => {
        if (mode === "register") {
          trackEvent(PlausibleEvent.SignupCompleted, { method: "email" });
          await applyPendingReferral();
        }
        navAfterAuth();
      },
      onError: (err: any) => {
        // F2: detect 2FA challenge from /login 401 response.
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.reason === "2fa_required" && detail.tmp_token) {
          setTwoFATmpToken(detail.tmp_token);
          return;
        }
        setError(typeof detail === "string" ? detail : t("err_generic"));
      },
    };
    if (mode === "register") {
      const lang = (i18n.language || "en").slice(0, 2);
      // W4.3 — the checkbox above this submit guards the gate, but we send
      // the flag explicitly so the backend can also enforce it server-side.
      register.mutate(
        { email, password, lang, consent_accepted: healthConsent },
        handlers,
      );
    } else {
      login.mutate({ email, password }, handlers);
    }
  };

  const submit2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!twoFATmpToken) return;
    const value = twoFACode.trim();
    if (!value) {
      setError(t("err_generic"));
      return;
    }
    const isBackup = value.includes("-");
    try {
      await twoFAChallenge.mutateAsync(
        isBackup
          ? { tmp_token: twoFATmpToken, backup_code: value }
          : { tmp_token: twoFATmpToken, code: value },
      );
      // Cookie is set by the server; force a profile re-fetch.
      await qc.invalidateQueries({ queryKey: ["me"] });
      navAfterAuth();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail === "invalid_2fa_challenge") {
        setError(t("err_generic"));
        setTwoFATmpToken(null);
      } else {
        setError(typeof detail === "string" ? detail : t("err_generic"));
      }
    }
  };

  const stopWaiting = (msg?: string) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = null;
    setWaitingForTg(false);
    setWaitingDeeplink(null);
    if (msg) setError(msg);
  };

  // Flip to the waiting screen and start polling for bot confirmation.
  // Does NOT navigate anywhere — the anchor's own default action opens TG.
  const beginPolling = (token: string, deeplink: string) => {
    sessionStorage.setItem("tg_login_token", token);
    sessionStorage.setItem("tg_login_started", String(Date.now()));
    setWaitingForTg(true);
    setWaitingDeeplink(deeplink);
    startedAtRef.current = Date.now();
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
        sessionStorage.removeItem("tg_login_token");
        sessionStorage.removeItem("tg_login_started");
        stopWaiting(t("err_timeout"));
        return;
      }
      try {
        const st = await pollTgLinkStatus(token);
        if (st.confirmed) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          sessionStorage.removeItem("tg_login_token");
          sessionStorage.removeItem("tg_login_started");
          const athlete = await tgLinkConsume.mutateAsync(token);
          qc.setQueryData(["me"], athlete);
          nav(redirectTo, { replace: true });
        }
      } catch (e: any) {
        if (e?.response?.status === 410) {
          sessionStorage.removeItem("tg_login_token");
          sessionStorage.removeItem("tg_login_started");
          stopWaiting(t("err_expired"));
        }
      }
    }, POLL_INTERVAL_MS);
  };

  // Telegram button tap. When the deeplink is pre-fetched (normal case) we let
  // the anchor navigate to TG natively and just kick off polling. If the
  // pre-fetch hasn't landed yet, fall back to fetch-then-same-tab-navigate
  // (mobile universal link keeps this tab alive; poll restores on return).
  const onTgClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    setError(null);
    if (tgToken && tgDeeplink) {
      beginPolling(tgToken, tgDeeplink);
      return; // anchor href opens TG
    }
    e.preventDefault();
    try {
      const { token, deeplink } = await tgLinkStart.mutateAsync();
      setTgToken(token);
      setTgDeeplink(deeplink);
      beginPolling(token, deeplink);
      window.location.href = deeplink;
    } catch (err: any) {
      setError(err?.response?.data?.detail || t("err_link"));
    }
  };

  const busy = login.isPending || register.isPending || tgLogin.isPending;

  /* ── Hero background — same recipe as LandingPage ─────────────────── */
  const HeroBg = () => (
    <>
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          backgroundImage: "url(/hero.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
          filter: "grayscale(1) brightness(0.38) contrast(1.18)",
        }}
      />
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,14,20,0.78) 0%, rgba(10,14,20,0.92) 50%, var(--bg-base) 100%)",
        }}
      />
    </>
  );

  /* ── States ───────────────────────────────────────────────────────── */
  if (autoTrying) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center px-4 relative">
        <HeroBg />
        <div className="text-center space-y-4 relative z-10">
          <Wordmark size="lg" />
          <div className="text-fg-secondary text-[13px]">{t("tg_authing")}</div>
        </div>
      </div>
    );
  }

  if (waitingForTg) {
    return (
      <div className="min-h-[100svh] flex flex-col items-center justify-center px-5 py-8 relative">
        <HeroBg />
        <div className="w-full max-w-sm relative z-10">
          <BackLink t={t} />
          <div className="text-center mb-8">
            <Wordmark size="md" />
          </div>
          <div className="indigo-card-glass p-6 space-y-5">
            <div className="flex justify-center">
              <div className="relative w-11 h-11">
                <div className="absolute inset-0 rounded-full border-2 border-bg-border" />
                <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
              </div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-[15px] font-semibold text-fg-primary">{t("tg_confirm")}</div>
              <p className="text-[13px] text-fg-secondary leading-relaxed">
                {t("tg_press_start_0")} <span className="text-fg-primary font-medium">{t("tg_press_start_1")}</span> {t("tg_press_start_2")}
              </p>
            </div>
            {waitingDeeplink && (
              <a
                href={waitingDeeplink}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center h-11 leading-[2.75rem] rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold transition-colors"
              >
                {t("tg_open")}
              </a>
            )}
            <button
              onClick={() => stopWaiting()}
              className="block mx-auto text-[12px] text-fg-muted hover:text-fg-secondary transition-colors"
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── W4.6 TG WebApp consent gate screen ───────────────────────────── */
  if (tgPendingInitData) {
    return (
      <main className="min-h-[100svh] flex flex-col items-center justify-center px-5 py-8 relative">
        <HeroBg />
        <div className="w-full max-w-sm relative z-10">
          <div className="text-center mb-6">
            <Wordmark size="md" />
          </div>
          <div className="indigo-card-glass p-6 space-y-4">
            <div className="text-[15px] font-semibold text-fg-primary text-center">
              {t("consent_title", { defaultValue: "Before we continue" })}
            </div>
            <p className="text-[12px] text-fg-secondary leading-relaxed">
              {t("health_consent_label")}
            </p>
            <label className="flex items-start gap-2 text-[12px] text-fg-secondary">
              <input
                type="checkbox"
                checked={healthConsent}
                onChange={(e) => setHealthConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span>{t("consent_accept_label", { defaultValue: "I accept terms, privacy and health data processing" })}</span>
            </label>
            {error && <div className="text-[12px] text-bad">{error}</div>}
            <button
              type="button"
              disabled={!healthConsent || tgLogin.isPending}
              onClick={() => {
                if (!healthConsent) {
                  setError(t("err_consent_required"));
                  return;
                }
                setError(null);
                acceptTgConsent();
              }}
              className="w-full h-11 rounded-pill bg-accent text-accent-on font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {tgLogin.isPending ? "…" : t("consent_continue", { defaultValue: "Continue" })}
            </button>
          </div>
        </div>
      </main>
    );
  }

  /* ── 2FA challenge screen ─────────────────────────────────────────── */
  if (twoFATmpToken) {
    return (
      <main className="min-h-[100svh] flex flex-col items-center justify-center px-5 py-8 relative">
        <HeroBg />
        <div className="w-full max-w-sm relative z-10">
          <button
            type="button"
            onClick={() => { setTwoFATmpToken(null); setTwoFACode(""); setError(null); }}
            className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg-primary mb-6"
          >
            <ArrowLeft size={14} />
            {t("two_fa.challenge_back", { ns: "settings", defaultValue: "Back to login" })}
          </button>
          <div className="text-center mb-6">
            <Wordmark size="md" />
          </div>
          <div className="indigo-card-glass p-6 space-y-4">
            <div className="text-[15px] font-semibold text-fg-primary text-center">
              {t("two_fa.challenge_title", { ns: "settings", defaultValue: "Enter your authenticator code" })}
            </div>
            <p className="text-[12px] text-fg-secondary text-center leading-relaxed">
              {t("two_fa.challenge_hint", { ns: "settings", defaultValue: "Open your authenticator app or use a backup code" })}
            </p>
            <form onSubmit={submit2FA} className="space-y-3">
              <label htmlFor="2fa-challenge-input" className="sr-only">
                {t("two_fa.confirm_label", { ns: "settings", defaultValue: "Code" })}
              </label>
              <input
                id="2fa-challenge-input"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456 / XXXXX-XXXXX"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)}
                aria-required="true"
                aria-invalid={error ? "true" : "false"}
                className="w-full h-11 px-3.5 rounded-pill bg-bg-base/60 border border-bg-border focus:border-accent focus:shadow-focus outline-none text-[15px] text-fg-primary text-center font-mono tracking-widest"
              />
              {error && (
                <div role="alert" className="text-[13px] text-bad bg-bad-subtle/30 border border-bad/30 rounded-pill px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={twoFAChallenge.isPending}
                className="w-full h-11 rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold disabled:opacity-50 transition-colors"
              >
                {twoFAChallenge.isPending ? "…" : t("two_fa.challenge_btn", { ns: "settings", defaultValue: "Verify" })}
              </button>
            </form>
            {/* A1: lost-access recovery link */}
            <div className="text-center text-[12px] text-fg-muted pt-1">
              {recoverySent ? (
                <span role="status" className="text-ok">
                  {t("two_fa.recovery_sent", { ns: "settings", defaultValue: "Recovery request sent. Storica will reach out." })}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    if (!email) {
                      setError(t("two_fa.recovery_need_email", { ns: "settings", defaultValue: "Enter your email above first" }));
                      return;
                    }
                    try {
                      await twoFARecovery.mutateAsync(email);
                      setRecoverySent(true);
                      setError(null);
                    } catch {
                      // Endpoint always 204 — ignore.
                      setRecoverySent(true);
                    }
                  }}
                  className="text-fg-muted hover:text-fg-secondary underline underline-offset-2"
                >
                  {t("two_fa.recovery_link", { ns: "settings", defaultValue: "Lost access?" })}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ── Main login screen ────────────────────────────────────────────── */
  return (
    <main className="min-h-[100svh] flex flex-col items-center px-5 py-6 relative">
      <HeroBg />

      {/* Top bar: back link */}
      <div className="w-full max-w-md relative z-10 mb-10 sm:mb-16">
        <BackLink t={t} />
      </div>

      {/* Wordmark + tagline */}
      <div className="w-full max-w-md relative z-10 text-center mb-10 sm:mb-12"
           style={{ animation: "fadeUp 0.5s ease both" }}>
        <Wordmark size="lg" />
        <div className="mt-4 text-[12px] uppercase tracking-[0.3em] text-fg-muted">
          {t("tagline")}
        </div>
      </div>

      {/* Auth card */}
      <div className="w-full max-w-sm relative z-10"
           style={{ animation: "fadeUp 0.55s 0.1s ease both" }}>

        {/* Mode switch — segmented control */}
        <div className="grid grid-cols-2 mb-5 rounded-pill border border-bg-border bg-bg-surface/70 backdrop-blur p-1 gap-1">
          <button
            type="button"
            onClick={() => { setMode("login"); setError(null); }}
            className={`py-2 text-[13px] font-medium rounded-[6px] transition-colors ${
              mode === "login"
                ? "bg-bg-raised text-fg-primary"
                : "text-fg-muted hover:text-fg-secondary"
            }`}
          >
            {t("tab_sign_in")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (mode !== "register") {
                trackEvent(PlausibleEvent.SignupStarted, { method: "email" });
              }
              setMode("register");
              setError(null);
            }}
            className={`py-2 text-[13px] font-medium rounded-[6px] transition-colors ${
              mode === "register"
                ? "bg-bg-raised text-fg-primary"
                : "text-fg-muted hover:text-fg-secondary"
            }`}
          >
            {t("tab_register")}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" aria-label={t(mode === "login" ? "tab_sign_in" : "tab_register")}>
          <input
            type="email"
            placeholder={t("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            aria-label={t("email")}
            aria-required="true"
            className="w-full h-11 px-3.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border focus:border-accent focus:shadow-focus outline-none text-[15px] text-fg-primary placeholder:text-fg-dim transition-shadow"
          />
          <input
            type="password"
            placeholder={t("password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            aria-label={t("password")}
            aria-required="true"
            className="w-full h-11 px-3.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border focus:border-accent focus:shadow-focus outline-none text-[15px] text-fg-primary placeholder:text-fg-dim transition-shadow"
          />

          {mode === "register" && (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={healthConsent}
                onChange={(e) => setHealthConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border border-bg-border accent-accent cursor-pointer"
              />
              <span className="text-[12px] text-fg-muted leading-snug">
                <Trans
                  i18nKey="health_consent"
                  ns="auth"
                  components={{
                    datenschutz: <Link to="/datenschutz" className="text-accent underline underline-offset-2" target="_blank" rel="noopener" />,
                  }}
                />
              </span>
            </label>
          )}

          {error && (
            <div className="text-[13px] text-bad bg-bad-subtle/30 border border-bad/30 rounded-pill px-3 py-2 leading-snug">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold disabled:opacity-50 transition-colors"
          >
            {busy ? "…" : mode === "login" ? t("sign_in_btn") : t("register_btn")}
          </button>
        </form>

        {mode === "login" && (
          <div className="mt-3 text-center">
            <Link
              to="/forgot-password"
              className="text-[12px] text-fg-muted hover:text-fg-secondary transition-colors"
            >
              {t("forgot_link")}
            </Link>
          </div>
        )}

        {/* Divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-bg-border" />
          <span className="text-[12px] uppercase tracking-[0.2em] text-fg-muted">{t("or")}</span>
          <div className="flex-1 h-px bg-bg-border" />
        </div>

        {/* Social — side-by-side (J-03 polish, AuthFinal layout) */}
        <div className="flex gap-2">
          <a
            href={tgDeeplink || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onTgClick}
            className="flex-1 min-w-0 h-11 flex items-center justify-center gap-2.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border hover:border-fg-secondary/40 text-fg-primary text-[14px] font-medium transition-colors"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="#229ED9" aria-hidden="true">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
            </svg>
            {t("telegram")}
          </a>

          {/* W4.5 — Google login button. In register mode we append
              ?consent=1 only after the user has ticked the DSGVO box;
              login mode (existing user) goes straight through, the
              backend gate only fires on new-account creation. */}
          <a
            href={`${import.meta.env.VITE_API_BASE || "http://localhost:8000"}/athlete/auth/google${
              mode === "register" && healthConsent ? "?consent=1" : ""
            }`}
            onClick={(e) => {
              if (mode === "register" && !healthConsent) {
                e.preventDefault();
                setError(t("err_consent_required"));
              }
            }}
            aria-disabled={mode === "register" && !healthConsent}
            className={`flex-1 min-w-0 h-11 flex items-center justify-center gap-2.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border hover:border-fg-secondary/40 text-fg-primary text-[14px] font-medium transition-colors ${
              mode === "register" && !healthConsent ? "opacity-60" : ""
            }`}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("google")}
          </a>
        </div>

        <p className="text-[12px] text-fg-muted text-center mt-6 leading-relaxed">
          {t("bot_hint")}
        </p>
      </div>

      {/* Bottom safe area */}
      <div className="flex-1 min-h-[20px]" />
    </main>
  );
}

/* ── tiny components ─────────────────────────────────────────────────── */

function Wordmark({ size }: { size: "md" | "lg" }) {
  const cls = size === "lg" ? "text-[44px] sm:text-[56px]" : "text-[32px]";
  return (
    <span className={`block font-black tracking-tight leading-none text-fg-primary ${cls}`}>
      <span className="text-accent">S</span>torica
    </span>
  );
}

function BackLink({ t }: { t: (key: string) => string }) {
  return (
    <Link
      to="/welcome"
      className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg-primary transition-colors"
    >
      <ArrowLeft size={14} />
      {t("back")}
    </Link>
  );
}
